import type { CSSProperties, HTMLAttributes } from "react";

export type ProgressTone = "gold" | "vine" | "aubergine" | "rust";

// Dégradé du tone `rust` (warning sérieux, ex. rendement au-dessus du butoir).
// Appliqué EN INLINE plutôt que via une classe CSS globale : ça garde le tone
// entièrement dans `@champ-erp/ui` (pas de règle à ajouter au styles.css de
// l'app). On réutilise les variables de palette globales (aucun hex hardcodé).
const RUST_GRADIENT = "linear-gradient(90deg, var(--color-rust-300), var(--color-rust-600))";

export interface ProgressBarProps extends HTMLAttributes<HTMLDivElement> {
  /** Valeur de remplissage en pourcentage (clampée 0..100). */
  value: number;
  /** Couleur du remplissage : gold (défaut) / vine (livrable) / aubergine. */
  tone?: ProgressTone;
  /** Libellé accessible (lu par les lecteurs d'écran). */
  label?: string;
}

/**
 * Barre de progression fine (6 px) utilisée pour les jauges de stock.
 * S'appuie sur `.ui-progress` + `.tone-${tone}` dans styles.css.
 *
 * Astuce gradient : on calcule `background-size` du fill (`<span>`) pour que
 * le gradient soit toujours étiré sur la largeur de la barre complète, pas
 * seulement sur la portion remplie. Résultat :
 * - 10 % rempli → on voit le début (clair) du gradient
 * - 50 % rempli → on voit clair → moyen
 * - 100 % rempli → gradient complet visible
 *
 * Sans cette astuce, le gradient était compressé dans la portion `<span>`
 * et apparaissait foncé même à 10 % de remplissage.
 */
export function ProgressBar({
  value,
  tone = "gold",
  label,
  className = "",
  ...rest
}: ProgressBarProps) {
  const clamped = Math.max(0, Math.min(100, value));
  const toneClass = tone === "gold" ? "" : `tone-${tone}`;
  const composed = ["ui-progress", toneClass, className].filter(Boolean).join(" ");

  // bg-size en pourcentage de la largeur du `<span>` :
  // 10 % rempli → bg-size 1000 % (le gradient s'étend sur 10× la largeur du
  //               span, donc seul le tout début est visible) ;
  // 100 % rempli → bg-size 100 % (le gradient occupe pile la largeur).
  // Si pct = 0, on évite la division par zéro (le span est invisible).
  const fillStyle = {
    width: `${clamped}%`,
    "--ui-progress-bg-size": clamped > 0 ? `${10000 / clamped}%` : "100%",
    // `rust` n'a pas de règle `.tone-*` en CSS → on pose son dégradé inline
    // (l'inline prime sur la classe `.ui-progress > span`).
    ...(tone === "rust" ? { backgroundImage: RUST_GRADIENT } : {}),
  } as CSSProperties;

  return (
    <div
      className={composed}
      role="progressbar"
      aria-valuenow={clamped}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={label}
      {...rest}
    >
      <span style={fillStyle} />
    </div>
  );
}
